import { useState, useCallback } from 'react';
import {
  Piece,
  PieceResponse,
  SearchPiecesRequest,
  ACLAuthorizationRequest,
  SearchPiecesPostContentTypeEnum,
  CreatePieceContentTypeEnum,
} from '@/api/bol';
import { useSnackbarNotification } from '@/hooks/useSnackbarNotification';
import { getPiecesApi } from '@/services/bolApiClient';
import { extractErrorMessage, formatOperationError } from '@/utils/errorUtils';
import { getLogisticsObjectDetailPath } from '@/types/logisticsObject';
import { getEffectiveLogisticsObjectSearchQuery } from '@/utils/searchDefaults';

export interface UsePiecesReturn {
  pieces: PieceResponse[];
  loading: boolean;
  searchError: string | null;
  selectedPiece: PieceResponse | null;
  setSelectedPiece: (piece: PieceResponse | null) => void;
  fetchPieces: (page?: number, pageSize?: number, searchBody?: SearchPiecesRequest) => Promise<boolean>;
  createPiece: (pieceData: unknown, memberIdentifier: string, aclAuthorizations?: ACLAuthorizationRequest[]) => Promise<void>;
  deletePiece: (pieceId: string) => Promise<void>;
  setTotal: (total: number) => void;
  total: number;
}

export const usePieces = (): UsePiecesReturn => {
  const [items, setItems] = useState<PieceResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedPiece, setSelectedPiece] = useState<PieceResponse | null>(null);
  const [total, setTotal] = useState(0);

  const { showSuccess, showError } = useSnackbarNotification();

  const fetchPieces = useCallback(async (page: number = 0, pageSize: number = 10, searchBody?: SearchPiecesRequest): Promise<boolean> => {
    const normalizedSearchBody = getEffectiveLogisticsObjectSearchQuery(searchBody);
    setLoading(true);
    setSearchError(null);

    try {
      const api = getPiecesApi();
      const response = await api.searchPiecesPost({
        contentType: SearchPiecesPostContentTypeEnum.ApplicationJson,
        searchPiecesRequest: normalizedSearchBody,
        limit: pageSize,
        offset: page * pageSize,
      });

      setItems(response.results || []);
      setTotal(response.pagination?.total || 0);
      return true;
    } catch (err: unknown) {
      const errorMessage = await extractErrorMessage(err, 'Failed to fetch pieces');
      setSearchError(errorMessage);
      showError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const createPiece = useCallback(async (pieceData: unknown, memberIdentifier: string, aclAuthorizations?: ACLAuthorizationRequest[]) => {
    try {
      const api = getPiecesApi();

      const createdPiece = await api.createPiece({
        contentType: CreatePieceContentTypeEnum.ApplicationJson,
        xBOLMemberIdentifier: memberIdentifier,
        piece: pieceData as Piece,
        source: true,
        xBOLACLAuthorizations: aclAuthorizations ? JSON.stringify(aclAuthorizations) : undefined,
      });

      const createdPieceId = createdPiece?.id;

      showSuccess(
        'Piece created successfully',
        createdPieceId
          ? {
              link: {
                label: 'View details',
                href: getLogisticsObjectDetailPath('piece', createdPieceId),
              },
            }
          : undefined,
      );
    } catch (err: unknown) {
      const errorMessage = await formatOperationError(err, 'Create piece', 'Failed to create piece');
      showError(errorMessage);
      throw err;
    }
  }, [showSuccess, showError]);

  const deletePiece = useCallback(async (pieceId: string) => {
    try {
      const api = getPiecesApi();
      await api.deletePieceById({ pieceId });
      showSuccess('Piece deleted successfully');
    } catch (err: unknown) {
      const errorMessage = await formatOperationError(err, 'Delete piece', 'Failed to delete piece');
      showError(errorMessage);
      throw err;
    }
  }, [showSuccess, showError]);

  return {
    pieces: items,
    loading,
    searchError,
    selectedPiece,
    setSelectedPiece,
    fetchPieces,
    createPiece,
    deletePiece,
    setTotal,
    total,
  };
};
